import { useEffect } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Card, CardContent, Field, Input } from "@fiskhub/ui";
import { ApiRequestError } from "@/api/client";
import { useLogin, useSession } from "@/api/hooks";

/**
 * Sign in.
 *
 * The session is a cookie set by the API — httpOnly, Secure, SameSite=Lax
 * (§14) — so nothing is stored here at all. `SameSite=Lax` is what keeps that
 * cookie off a cross-site request; the CSRF token that used to back it up was
 * removed at the owner's request.
 *
 * One step: email and password in, session out. §14 also asks for TOTP on every
 * dashboard user; that was removed at the owner's request, so there is no
 * second-factor field and no second round trip.
 *
 * ## Choosing a tenant
 *
 * `/login?slug=acme` names the organisation to sign in to, and the slug is sent
 * as `tenant_slug` in the login body. It is needed because logins are per
 * tenant — `users` is `UNIQUE (tenant_id, email)` — so the same address can
 * exist in two organisations and the API will not guess between them. Without
 * the parameter the API still resolves the tenant from the subdomain
 * (`acme.admin.…`) or from the sole active tenant, so a single-tenant install
 * and a per-tenant subdomain both keep working untouched.
 *
 * The slug is a *routing* hint, not a credential: it only decides which tenant
 * the email is looked up in. It grants nothing on its own — a wrong slug is an
 * ordinary failed sign-in — and it is not stored anywhere on this side. The
 * tenant the session actually belongs to is fixed by the API at login and
 * carried in the signed session cookie, not by this URL.
 */

const loginSchema = z.object({
  email: z.string().min(1, "Enter your email address.").email("That is not a valid email address."),
  password: z.string().min(8, "Passwords are at least 8 characters."),
});

type LoginValues = z.infer<typeof loginSchema>;

export function LoginScreen() {
  const navigate = useNavigate();
  const { slug } = useSearch({ from: "/login" });
  const session = useSession();
  const login = useLogin();

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  useEffect(() => {
    if (session.data) void navigate({ to: "/" });
  }, [session.data, navigate]);

  const onSubmit = handleSubmit((values) => {
    clearErrors("root");
    login.mutate(
      // Omitted rather than sent empty when there is no `?slug=`: the endpoint
      // treats an absent `tenant_slug` as "resolve it yourself", which is what
      // a subdomain or single-tenant deployment needs.
      { email: values.email, password: values.password, ...(slug ? { tenant_slug: slug } : {}) },
      {
        onSuccess: () => {
          void navigate({ to: "/" });
        },
        onError: (error) => {
          if (error instanceof ApiRequestError) {
            if (error.param === "tenant_slug") {
              // Not a field on this form — attaching it to the password input
              // would blame the wrong thing. The problem is the link.
              setError("root", { type: "server", message: error.message });
              return;
            }
            const field: keyof LoginValues = error.param === "email" ? "email" : "password";
            setError(field, { type: "server", message: error.message });
          } else {
            setError("password", { type: "server", message: "Sign-in failed. Try again." });
          }
        },
      },
    );
  });

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">FiskHub</h1>
          <p className="text-sm text-muted-foreground">
            Croatian fiscalization for unattended payment devices.
          </p>
          {slug ? (
            // Shown because the tenant came from the URL rather than from
            // anything the user typed: signing in to the wrong organisation
            // otherwise looks identical to signing in to the right one.
            <p className="text-sm text-muted-foreground">
              Signing in to <span className="font-medium text-foreground">{slug}</span>.
            </p>
          ) : null}
        </div>

        <Card>
          <CardContent className="pt-5">
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              {errors.root?.message ? (
                <p role="alert" className="text-sm text-destructive">
                  {errors.root.message}
                </p>
              ) : null}

              <Field
                id="login-email"
                label="Email"
                required
                error={errors.email?.message}
              >
                {(aria) => (
                  <Input
                    {...aria}
                    type="email"
                    autoComplete="username"
                    autoFocus
                    {...register("email")}
                  />
                )}
              </Field>

              <Field id="login-password" label="Password" required error={errors.password?.message}>
                {(aria) => (
                  <Input
                    {...aria}
                    type="password"
                    autoComplete="current-password"
                    {...register("password")}
                  />
                )}
              </Field>

              <Button type="submit" className="w-full" loading={isSubmitting || login.isPending}>
                Sign in
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          Sessions are cookie-based and expire on sign-out.
        </p>
      </div>
    </main>
  );
}
